import { Stack, Typography } from '@mui/joy';
import { Plus } from '@phosphor-icons/react';
import { upperFirst } from 'lodash';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ContactGroupType } from 'contacts/enums';
import { customerContactListPath, vendorContactListPath } from 'contacts/routes/routePaths';
import { Button, FormField } from 'core/components';
import { Form } from 'core/components';
import { NotificationElement } from 'core/components/Notification';
import { NotificationPosition } from 'core/enums';
import { ApiStatus } from 'core/enums';
import { InputType } from 'core/enums/input';
import { useFormContext } from 'core/states';
import { Color, Level, Size } from 'theme/enums';

import { AddContactGroupByEmailTrigger } from '../../enums';
import { useAddContactGroupByEmailContext } from '../../state';

export const ContactEmailFormFields = forwardRef(function ContactEmailFormFields(_, ref) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { trigger, getValues, setValue } = useFormContext();

  const { contactGroup, contactGroupType, contactFromOtherContactGroupType } =
    useAddContactGroupByEmailContext();

  const otherContactGroupType =
    contactGroupType === ContactGroupType.CUSTOMER
      ? ContactGroupType.VENDOR
      : ContactGroupType.CUSTOMER;

  useImperativeHandle(
    ref,
    () => ({
      triggerFormSubmit: () => {
        return new Promise((resolve, reject) => {
          trigger()
            .then((isValid) => {
              if (isValid) {
                resolve(getValues());
              } else {
                reject();
              }
            })
            .catch(reject);
        });
      },
      setContactEmail: (email: string) => {
        setValue('email', email);
      },
    }),
    [getValues, setValue, trigger]
  );

  const differentContactGroupTypeMessage = useMemo(() => {
    const buttonLabel =
      otherContactGroupType === ContactGroupType.CUSTOMER
        ? t('buttonLabelAddCustomer')
        : t('buttonLabelAddVendor');

    const goToPath =
      otherContactGroupType === ContactGroupType.CUSTOMER
        ? customerContactListPath(AddContactGroupByEmailTrigger.ADD_CUSTOMER)
        : vendorContactListPath(AddContactGroupByEmailTrigger.ADD_VENDOR);

    return (
      <Stack gap={2} mt={2}>
        <Typography level={Level.TITLE_SM} color={Color.DANGER}>
          <Trans
            i18nKey="messageAddContactGroupByEmailOtherTypeFound"
            values={{
              contactGroupType: upperFirst(otherContactGroupType),
              contactGroupName: contactGroup?.name,
            }}
          />
        </Typography>

        <Button
          text={buttonLabel}
          size={Size.SM}
          startDecorator={<Plus />}
          onClick={() => navigate(goToPath)}
          sx={{ width: 'fit-content' }}
        />
      </Stack>
    );
  }, [contactGroup?.name, navigate, otherContactGroupType, t]);

  return (
    <Form isOpen inline withoutButtons withoutNotification>
      <NotificationElement
        title={t('titleAddContactGroupByEmailOtherTypeFound', {
          contactGroupType: upperFirst(otherContactGroupType),
        })}
        message={differentContactGroupTypeMessage}
        type={ApiStatus.ERROR}
        position={NotificationPosition.DRAWER}
        showNotification={contactFromOtherContactGroupType}
        withoutCloseButton
      />

      <FormField
        name="email"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelContactEmail'),
          required: true,
        }}
      />
    </Form>
  );
});
